/**
 * Consumer · Lane Prices — what one lane pays, from the public Market Intel API.
 *
 * ONE WAY OF PRICING A LANE. This page used to aggregate `loadpipe_intel_hourly`
 * per request (5.4 s measured) with its own percentile walk. It now asks
 * `POST /market-intel/lanes/price/v1` — the same call load search, admin and
 * the agents make — so what an operator sees here is what a dispatcher sees.
 *
 * THE LANE IS TWO POINTS. The API prices coordinates, not cells; deriving the
 * cell and choosing the tier is the module's job. The lane lives in the URL
 * (`?o=lat,lon&d=lat,lon&equipment=V`) so a lane can be linked to — the Heat
 * Map's destination list links here that way.
 *
 * THE TIER THAT ANSWERED IS SHOWN, ALWAYS. The module answers at the finest
 * tier with enough loads; an r2 answer covers ~87,000 km² and must not read as
 * precise as an r4 one.
 */

import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import Chip from '@mui/joy/Chip'
import Input from '@mui/joy/Input'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Typography from '@mui/joy/Typography'
import type { MarketIntelLaneQuery, MarketIntelLaneRate } from '@numeo/types-core'
import * as React from 'react'
import { useSearchParams } from 'react-router-dom'
import { marketIntelApi } from 'api/marketIntel'
import { AppPeriodsCard } from './components/AppPeriodsCard'
import { LaneDaysTable } from './components/LaneDaysTable'
import { LaneSeriesChart } from './components/LaneSeriesChart'
import { EquipmentSelect, SnapshotAge, TierChip, maxMoney, money, totalMoney } from './components/marketIntel'
import { ErrorState, Loading, Stat, StatRow, formatNumber, useLoadPipelineData } from './components/shared'

/** A few well-known lanes, so the page answers something before anything is typed. */
const PRESETS = [
    { label: 'Chicago, IL → Dallas, TX', o: '41.8781,-87.6298', d: '32.7767,-96.7970' },
    { label: 'Atlanta, GA → Chicago, IL', o: '33.7490,-84.3880', d: '41.8781,-87.6298' },
    { label: 'Los Angeles, CA → Phoenix, AZ', o: '34.0522,-118.2437', d: '33.4484,-112.0740' },
    { label: 'Dallas, TX → Atlanta, GA', o: '32.7767,-96.7970', d: '33.7490,-84.3880' },
]

function parsePoint(raw: string | null): { lat: number; lon: number } | null {
    const [lat, lon] = (raw ?? '').split(',').map((v) => Number(v.trim()))
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return null
    return { lat, lon }
}

function LaneRate({ lane, rate, minSamples }: { lane: MarketIntelLaneQuery; rate: MarketIntelLaneRate; minSamples: number }) {
    const delta = rate.todayP50 == null ? null : (rate.todayP50 - rate.p50) / rate.p50
    const todayTotal = rate.days.find((d) => d.daysAgo === 0)?.totalP50 ?? null

    return (
        <>
            <StatRow>
                <Stat label="Median (p50)" value={`${money(rate.p50)}/mi`} hint={`p25 ${money(rate.p25)} · p75 ${money(rate.p75)}`} />
                <Stat label="Realistic range" value={`${money(rate.p5)} – ${money(rate.p95)}`} hint="p5 – p95" />
                <Stat label="Extremes" value={`${money(rate.minRpm)} – ${maxMoney(rate.maxRpm)}`} hint="min – max, one load each" />
                <Stat
                    label="Today vs window"
                    value={rate.todayP50 == null ? '—' : `${money(rate.todayP50)}/mi`}
                    hint={
                        rate.todayP50 == null || delta == null
                            ? 'today is below the sample floor so far'
                            : `${delta >= 0 ? '+' : ''}${(delta * 100).toFixed(1)}% vs window median`
                    }
                    tone={delta == null ? 'neutral' : delta >= 0 ? 'success' : 'warning'}
                />
                <Stat
                    label="Median total"
                    value={totalMoney(rate.totalP50)}
                    hint={
                        rate.totalP50 == null
                            ? `withheld: fewer than ${minSamples} loads stated a total`
                            : `today ${todayTotal == null ? 'below floor' : totalMoney(todayTotal)} · band-resolution figure`
                    }
                />
                <Stat label="Loads" value={formatNumber(rate.observations)} hint={`avg ${money(rate.avgRpm)}/mi`} />
            </StatRow>

            <AppPeriodsCard lane={lane} />

            <Card sx={{ mt: 2 }}>
                <Typography level="title-md">Daily median</Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
                    Oldest first. A day below the sample floor has no median and is drawn as a gap; its bar still shows how many loads it had.
                </Typography>
                <LaneSeriesChart days={rate.days} />
            </Card>

            <Card sx={{ mt: 2 }}>
                <Typography level="title-md">Daily figures</Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1 }}>
                    Exactly as the snapshot holds them. A median needs {minSamples} loads that day ({minSamples} that stated a total, for the total); the
                    average and the count are kept below that. Implied miles = median total ÷ median $/mi — a sanity check against the trip length, not a
                    distance.
                </Typography>
                <LaneDaysTable days={rate.days} />
            </Card>
        </>
    )
}

export default function LoadPipelineLanePricesPage() {
    const [params, setParams] = useSearchParams()
    const o = params.get('o') ?? PRESETS[0].o
    const d = params.get('d') ?? PRESETS[0].d
    const equipment = params.get('equipment') ?? 'V'

    const [originText, setOriginText] = React.useState(o)
    const [destText, setDestText] = React.useState(d)
    React.useEffect(() => {
        setOriginText(o)
        setDestText(d)
    }, [o, d])

    const origin = parsePoint(o)
    const dest = parsePoint(d)
    const lane: MarketIntelLaneQuery | null =
        origin && dest ? { originLat: origin.lat, originLon: origin.lon, destLat: dest.lat, destLon: dest.lon, equipment } : null

    const { data, error, loading } = useLoadPipelineData(async () => (lane ? marketIntelApi.priceLanes([lane]) : null), [o, d, equipment])
    const rate = data?.lanes[0] ?? null

    const go = (next: { o?: string; d?: string; equipment?: string }) => setParams({ o, d, equipment, ...next })
    const preset = PRESETS.find((p) => p.o === o && p.d === d)

    return (
        <Box>
            <Typography level="body-sm" sx={{ mb: 2, color: 'text.tertiary' }}>
                What one lane pays, from the Market Intel API — the same answer load search shows. Pick a preset, type two points, or follow a destination from
                the Heat Map.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Select
                    size="sm"
                    placeholder="Preset lane"
                    value={preset?.label ?? null}
                    onChange={(_, v) => {
                        const p = PRESETS.find((x) => x.label === v)
                        if (p) go({ o: p.o, d: p.d })
                    }}
                    sx={{ minWidth: 260 }}
                >
                    {PRESETS.map((p) => (
                        <Option key={p.label} value={p.label}>
                            {p.label}
                        </Option>
                    ))}
                </Select>
                <Input
                    size="sm"
                    value={originText}
                    onChange={(e) => setOriginText(e.target.value)}
                    startDecorator="From"
                    placeholder="lat,lon"
                    sx={{ width: 220 }}
                />
                <Input size="sm" value={destText} onChange={(e) => setDestText(e.target.value)} startDecorator="To" placeholder="lat,lon" sx={{ width: 220 }} />
                <EquipmentSelect value={equipment} onChange={(v) => go({ equipment: v })} />
                <Button size="sm" disabled={!parsePoint(originText) || !parsePoint(destText)} onClick={() => go({ o: originText.trim(), d: destText.trim() })}>
                    Price lane
                </Button>
            </Box>

            {!lane ? (
                <Typography level="body-sm" color="warning">
                    Both points must be <code>lat,lon</code>.
                </Typography>
            ) : null}
            {loading ? <Loading /> : null}
            {error ? <ErrorState error={error} /> : null}

            {!loading && !error && data ? (
                <>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography level="title-md">{preset?.label ?? `${o} → ${d}`}</Typography>
                        {rate ? <TierChip tier={rate.tier} /> : null}
                        {rate ? (
                            <Chip size="sm" variant="outlined">
                                {rate.equipment}
                                {rate.equipment !== equipment ? ` (for ${equipment})` : ''}
                            </Chip>
                        ) : null}
                        <SnapshotAge provenance={data} />
                    </Box>
                    {rate && lane ? (
                        <LaneRate lane={lane} rate={rate} minSamples={data.minSamples ?? 10} />
                    ) : (
                        <Card variant="soft" color="neutral">
                            <Typography level="title-sm">Not enough market data</Typography>
                            <Typography level="body-sm">
                                Fewer than {data.minSamples ?? 10} {equipment} loads on this lane at every tier over the {data.window ?? '14d'} window, so
                                nothing is published.
                            </Typography>
                        </Card>
                    )}
                </>
            ) : null}
        </Box>
    )
}
